import { create } from 'zustand'

export interface Toast {
  id: string
  title: string
  description?: string
  tone: 'success' | 'error' | 'warning' | 'info'
  action?: { label: string; onClick: () => void }
}

interface ToastState {
  toasts: Toast[]
  push: (toast: Omit<Toast, 'id'> & { durationMs?: number }) => string
  dismiss: (id: string) => void
}

/**
 * Toasts live in a store rather than context so a mutation handler or an API
 * error boundary can raise one without being inside a provider's subtree.
 *
 * Errors do not auto-dismiss. A success message the user misses costs nothing;
 * a failure message they miss costs them the knowledge that it failed.
 */
export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: ({ durationMs, ...toast }) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    set((state) => ({ toasts: [...state.toasts, { ...toast, id }] }))
    const ttl = durationMs ?? (toast.tone === 'error' ? 0 : 4_000)
    if (ttl > 0) setTimeout(() => get().dismiss(id), ttl)
    return id
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}))

/** Convenience helpers so callers do not repeat the tone literal. */
export const toast = {
  success: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'success' }),
  error: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'error' }),
  warning: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'warning' }),
  info: (title: string, description?: string) =>
    useToastStore.getState().push({ title, description, tone: 'info' }),
}
